// Routes the Worklist page reads and writes: src/aithora/v3/worklist/items.ts (Measures,
// exits, capture, custom items, at risk, board, shift handoff), src/aithora/router.ts
// (care requests, med pass, nutrition, benefits, the mandatory-reporter referrals) and
// src/aithora/v3/finance/performance.ts (the fee schedule).
// Every route and field here is served (be-care 2026-09-30: the tabs beyond Measures live
// in src/aithora/v3/worklist/queues.ts). The page still renders its designed empty state
// when a building has no rows.
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import { feeSchedule } from './capture-routes';
import { worklistCover, worklistCoverAccept, worklistCoverPass, worklistCoverTakeBack, facilityCoverage, type CoverageLive } from './cover-routes';
import { facilityMedWatches, medWatchStepDone, medWatchClose, medWatchSign } from './med-watch-routes';

const A = AITHORA_PREFIX;

// ── Measures (the day's items) ──────────────────────────────────────────────

export type WorklistStatus = 'open' | 'snoozed' | 'done' | 'refused';
export type WorklistActionKind = 'DONE' | 'SNOOZE' | 'HANDOFF' | 'REFUSED' | 'UNDO';

/** V7 task ladder (served by v3/worklist/ladder-rule.ts LadderView). */
export type TaskLadderLevel = 'urgent' | 'soon' | 'routine';
export type TaskLadderStep = 'owner' | 'charge_nurse' | 'on_call_nurse' | 'everyone';
export interface TaskLadderView {
  level: TaskLadderLevel;
  steps: TaskLadderStep[];
  minutes: number[];
  startedAt: string;
  /** The last step the server took (0: still the owner's). */
  step: number;
  /** When each step up to `step` was taken (index 0 is the start). */
  stepAt: string[];
  nextMoveAt: string | null;
  /** Someone took it: the clock is stopped. */
  claimedBy: { userId: string | null; name: string | null } | null;
  claimedAt: string | null;
  closed: boolean;
  /** The server runs the ladder (WORKLIST_LADDER=on). Off: nothing moves on by itself and nextMoveAt is null. */
  ladderOn?: boolean;
}

/** One of today's items (WorkItem in items.ts). */
export interface WorklistItem {
  id: string;
  occurrence: string;
  itemRef: { tab: 'tasks'; sourceId: string; occurrence: string };
  residentId: string | null;
  residentName: string | null;
  room: string | null;
  domain: string | null;
  task: string;
  /** The item's own instructions, the line under it on the phone (null when none were written). */
  detail?: string | null;
  code: string | null;
  rateCents: number | null;
  rateStatus: 'VERIFIED' | 'ESTIMATED' | null;
  billingClaim: boolean;
  track: 'DRIVE' | 'DETECT' | null;
  captureType: 'number' | 'score' | 'yesno';
  unit: string | null;
  scheduledAt: string;
  dueAt: string;
  riskWindowAt: string;
  ownerId: string | null;
  ownerRole: string | null;
  isUnowned: boolean;
  source: 'builtin' | 'custom' | 'catalog';
  status: WorklistStatus;
  late: boolean;
  snoozeUntil: string | null;
  snoozeCount: number;
  lastAction: { id: string; action: WorklistActionKind; at: string; auto: boolean; actorName?: string | null; reason?: string | null } | null;
  lastCapture: { value: string; capturedBy: string | null; capturedAt: string } | null;
  /** The owner's display name when the owner is a person. */
  ownerName?: string | null;
  /** V7: the task ladder the server runs (v3/worklist/ladder-rule.ts). Every screen draws this, never its own clock. */
  ladder?: TaskLadderView;
  level?: TaskLadderLevel;
  /** 0 = still the owner's, up to the last step (everyone on shift). */
  ladderStep?: number;
  /** When the server moves it next (ISO); null when it will not move again. */
  nextMoveAt?: string | null;
  /** The server runs the ladder (WORKLIST_LADDER=on); off, nothing moves on by itself. */
  ladderOn?: boolean;
  /** Who had it before the ladder first moved it: a person's id, or null for a role. */
  firstOwnerId?: string | null;
  /** That person's name, or the role in words ("floor staff"). */
  firstOwnerName?: string | null;
}

export interface MeasuresResponse {
  real: true;
  facilityId: string;
  day: string;
  timezone: string;
  readOnly: boolean;
  /** Whether the server runs the task ladder (WORKLIST_LADDER=on). */
  ladderOn?: boolean;
  domains: { id: string; label: string; order: number; measures: unknown[] }[];
  items: WorklistItem[];
  counts: { open: number; late: number; snoozed: number; done: number; refused: number };
}

/** GET /facilities/:id/worklist/measures: the registry and today's items for the viewer. */
export const worklistMeasures = route<MeasuresResponse, { date?: string }>()('GET', `${A}/facilities/:id/worklist/measures`, { honest: true });

export interface WorklistCode {
  code: string;
  codeSystem: string | null;
  shortDesc: string | null;
  longDesc: string | null;
  rateCents: number | null;
  rateStatus: 'VERIFIED' | 'ESTIMATED' | null;
  /** In the registry or a curated category: the facility set (performed but not captured). */
  performedNotCaptured: boolean;
  /** The rate's period ('mo', '6mo', 'yr') or null for per occasion (billing-period.ts). */
  rateUnit?: string | null;
}

/** GET /worklist/codes?q=&limit=: code search over every code held. */
export const worklistCodes = route<{ real: true; catalogSize: number; codes: WorklistCode[] }, { q?: string; limit?: number }>()('GET', `${A}/worklist/codes`, { honest: true });

// FeeScheduleCode and feeSchedule (GET /billing/fee-schedule) are defined once, in
// capture-types.ts and capture-routes.ts.

/** POST /worklist/items/:id/capture {value}: record the value and close the item. */
export const worklistCapture = route<{ real: true; value: string; capturedBy: string | null; capturedAt: string; actionId: string }, void, { value: string | number | boolean; occurrence?: string }>()(
  'POST', `${A}/worklist/items/:id/capture`, { honest: true });

/** An item reference for an exit. tasks, medpass and notes are per day (occurrence); the others are not. */
export interface ItemRef {
  tab: 'tasks' | 'alerts' | 'requests' | 'medpass' | 'notes' | 'nutrition' | 'benefits' | 'referrals';
  sourceId: string;
  occurrence?: string | null;
}

export interface ActionBody {
  itemRef?: ItemRef;
  itemRefs?: ItemRef[];
  action: WorklistActionKind;
  reason?: string;
  snoozeUntil?: string;
  handedTo?: { userId?: string; role?: string };
}

export interface ActionsResponse {
  real: true;
  applied: number;
  failed: number;
  results: ({ ok: true; actionId: string; status: WorklistStatus } | { ok: false; status: number; error: string })[];
}

/** POST /worklist/actions: one or many exits (done, snooze, hand off, refused, undo). */
export const worklistActions = route<ActionsResponse, void, ActionBody>()('POST', `${A}/worklist/actions`, { honest: true });

export interface NewItemBody {
  what: string;
  residentId?: string | null;
  code?: string | null;
  ownerUserId?: string | null;
  ownerRole?: string | null;
  cadence?: 'once' | 'daily' | 'weekly' | 'monthly';
  dueWindowHours?: number;
  /** HH:MM on the building's clock; omitted, the item is all day. */
  timeOfDay?: string;
  /** VITALS makes it a vitals stop (Record on the shift); omitted, a care task. */
  taskType?: 'VITALS';
}

/** POST /facilities/:id/worklist/items: a facility's own item. */
export const worklistCreateItem = route<{ real: true; item: unknown }, void, NewItemBody>()('POST', `${A}/facilities/:id/worklist/items`, { honest: true });

export interface AtRiskResponse {
  real: true;
  modeled: true;
  basis: 'modeled';
  rule: string;
  horizon: string;
  lateCount: number;
  /**
   * Why each item is predicted to slip: time_left (minutes), items_ahead (count),
   * snooze_count, owner; handed_to (text: who it was handed to). Items come from every tab
   * with a real deadline (Measures, Med pass, Part A notes, Benefits due today, draft
   * Referrals for the Facility Admin); itemRef.tab says which.
   */
  items: (WorklistItem & { reasons: { key: string; text: string; value: number | string | null }[] })[];
}

/** GET /worklist/at-risk?facility=: items predicted to slip this shift (MODELED), across tabs. */
export const worklistAtRisk = route<AtRiskResponse, { facility?: string }>()('GET', `${A}/worklist/at-risk`, { honest: true });

export interface BoardFacility {
  facilityId: string;
  name: string | null;
  day: string;
  total: number;
  done: number;
  donePct: number | null;
  overdue: number;
  snoozed: number;
  atRisk: number;
}

/** GET /worklist/board?facilities=: per facility done %, overdue, at risk (facilities: the switcher's pick, Super Admin and Operator only). */
export const worklistBoard = route<{ real: true; day: string | null; readOnly: boolean; atRiskBasis: 'modeled'; rule: string; facilities: BoardFacility[] }, { facilities?: string }>()(
  'GET', `${A}/worklist/board`, { honest: true });

/** POST /worklist/handoff {summary, toRole}: store the shift summary, tell the incoming charge nurse. */
export const worklistHandoff = route<{ real: true; id: string; notified: { userId: string }[]; notifiedBuilding: boolean; reason: string | null }, void, { summary: string; toRole?: string }>()(
  'POST', `${A}/worklist/handoff`, { honest: true });

export interface OnShiftPerson {
  userId: string;
  name: string | null;
  role: string;
  onCallRoles: string[];
}

/** GET /me/on-shift-with-me: who else is on shift in my building. */
export const onShiftWithMe = route<{ real?: true; people: OnShiftPerson[]; reason?: string }>()('GET', `${A}/me/on-shift-with-me`);

// ── Requests ────────────────────────────────────────────────────────────────

export interface CareRequest {
  id: string;
  resident_id: string | null;
  type: string;
  urgency: 'URGENT' | 'PROMPT' | string;
  description: string | null;
  status: 'OPEN' | 'ACKNOWLEDGED' | 'DONE' | string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  completed_by: string | null;
  completed_at: string | null;
  created_at: string;
  title: string;
  residentName?: string | null;
  room?: string | null;
  /** Aithora across buildings: the building, shown where a room would be for a masked resident. */
  facilityName?: string | null;
  /** Where it came in: the call button, Atlas, or family concierge. */
  source?: 'call_button' | 'companion' | 'family_concierge' | null;
  /** V7: the task ladder the server runs (v3/worklist/ladder-rule.ts). Every screen draws this, never its own clock. */
  ladder?: TaskLadderView;
  level?: TaskLadderLevel;
  /** 0 = still the owner's, up to the last step (everyone on shift). */
  ladderStep?: number;
  /** When the server moves it next (ISO); null when it will not move again. */
  nextMoveAt?: string | null;
  /** The server runs the ladder (WORKLIST_LADDER=on); off, nothing moves on by itself. */
  ladderOn?: boolean;
}

/** GET /care-requests?status=all: the building's requests, with a top-level `real`. */
export const careRequests = route<{ careRequests: CareRequest[]; real?: boolean; ladderOn?: boolean }, { status?: string; organizationId?: string }>()('GET', `${A}/care-requests`);
export const careRequestAcknowledge = route<{ ok: boolean }, void, { organizationId?: string } | undefined>()('POST', `${A}/care-requests/:id/acknowledge`);
export const careRequestComplete = route<{ ok: boolean }, void, { organizationId?: string } | undefined>()('POST', `${A}/care-requests/:id/complete`);

// ── Med pass ────────────────────────────────────────────────────────────────

/** How an order is given (medications.service_level): Administer (the default), Assistance
 *  (staff assist with self-administration and record Assisted), Self-administered (the
 *  resident takes it; only an observation note), Third party (not on the facility's pass). */
export type MedServiceLevel = 'administer' | 'assistance' | 'self_administered' | 'third_party';

export interface MedPassRow {
  medication_id: string;
  resident_id: string;
  name: string | null;
  dosage: string | null;
  frequency: string | null;
  resident_name: string | null;
  /** OBSERVED: an observation note on a self-administered order. */
  today_status: 'GIVEN' | 'MISSED' | 'REFUSED' | 'HELD' | 'OBSERVED' | null;
  /** Today's record was an Assisted outcome (GIVEN = assisted, taken). */
  today_assisted?: boolean;
  /** How the order is given; a third-party order is never on the pass. Absent reads as administer. */
  service_level?: MedServiceLevel;
  administered_at: string | null;
  room?: string | null;
  /** The scheduled time of today's next dose (medication_reminders); null when none is set. */
  dueAt?: string | null;
  /** Today's latest dose record (not struck out), for Undo and Strike Out. */
  administration_id?: string | null;
}

/** What a dose still needs before it is recorded (AF-1611, med-pass-rules.ts). */
export type MedPassNeed = 'reason' | 'preAdmin' | 'controlledCount' | 'controlledWitness' | 'prnReason' | 'prnResponse' | 'timingReason' | 'scan' | 'note';
/** POST /med-pass 422: the order's facts the sheet shows, and what is missing. */
export interface MedPassNeeds {
  error: string;
  needs: MedPassNeed[];
  order: { prn: boolean; holdParameter: string | null; preAdminCheck: string | null; controlled: boolean; timeCritical: boolean; dueAt: string | null; minutesFromDue: number | null };
  policy: { controlledCheck: 'none' | 'count' | 'witness' | 'count_and_witness'; windowMin: number; timeCriticalWindowMin: number; requireScan?: boolean; sampleHoldRules?: boolean; scanning?: boolean; scanSkipReasons?: string[] };
  reasons: { code: string; label: string; status: 'MISSED' | 'REFUSED' | 'HELD' }[];
}
export interface MedPassRecordBody {
  medicationId: string;
  residentId: string;
  /** OBSERVED: an observation note, only on a self-administered order (with `note`). */
  status: 'GIVEN' | 'MISSED' | 'REFUSED' | 'HELD' | 'OBSERVED';
  medicationName?: string | null;
  reason?: string;
  timingReason?: string;
  prnReason?: string;
  /** An Assisted outcome (assistance with self-administration): only on an order set to Assistance. GIVEN = taken. */
  assisted?: boolean;
  /** An assisted PRN dose: how the resident responded. */
  prnResponse?: string;
  /** The observation note (OBSERVED) or a note on the dose. */
  note?: string;
  preAdmin?: string;
  controlledCount?: string;
  witnessUserId?: string;
  /** The passing scan for this dose (POST /med-pass/scan, med-tools-routes.ts); required for a Given when the building sets requireScan. */
  scanId?: string;
  /** The licensed nurse who authorized this PRN dose, when the building's rules need one (med-rules/). */
  prnAuthorizedBy?: string;
}
/** Who may give which medicine (med-rules/): a 403 refusal's body, or the warning a 'warn' building records. */
export interface MedRuleRefusal {
  error: string;
  medRule: { code: string; citation: { id: string; law: string; title: string; url: string | null } | null; ruleSet: string; credential: string; mode: 'enforce' | 'warn' };
}
export interface MedRuleWarning { reason: string; code: string; citation: { id: string; law: string; title: string; url: string | null } | null; ruleSet: string }

/** GET /med-pass: every resident medication and today's status, with a top-level `real`. */
export const medPass = route<{ meds: MedPassRow[]; real?: boolean }, { organizationId?: string }>()('GET', `${A}/med-pass`);

/** POST /med-pass: record ONE dose, given or not given. 422 (MedPassNeeds) when the order needs more first; 403 (MedRuleRefusal) when this person may not give it. */
export const medPassRecord = route<{ id: string; status: string; assisted?: boolean; undoUntil: string; effectivenessDueAt: string | null; medRuleWarning?: MedRuleWarning }, void, MedPassRecordBody>()(
  'POST', `${A}/med-pass`);
/** POST /med-pass/:id/undo: within 10 seconds, by the person who recorded it. 409 once it has run out. */
export const medPassUndo = route<{ ok: true; undone: true }>()('POST', `${A}/med-pass/:id/undo`);
/** POST /med-pass/:id/strike {reason, note?}: after the Undo, the only fix; the original stays, struck. */
export const medPassStrike = route<{ ok: true; struckAt: string }, void, { reason: string; note?: string }>()('POST', `${A}/med-pass/:id/strike`);

// ── Part A notes ────────────────────────────────────────────────────────────

export interface PartANoteRow {
  residentId: string;
  name: string | null;
  room: string | null;
  /** The last signed skilled note; null = none yet. */
  lastSignedAt: string | null;
  /** What the next note must carry that the record does not have yet. */
  missing: string[];
  signedNow?: boolean;
  /** Aithora across buildings: the building, where a room would be. */
  facilityName?: string | null;
}

/** GET /worklist/part-a?facility=: Part A residents and hours since the last signed note. */
export const partANotes = route<{ real: true; rows: PartANoteRow[] }, { facility?: string }>()('GET', `${A}/worklist/part-a`, { honest: true });

/** POST /worklist/part-a/:residentId/sign: Atlas drafts, a clinician signs. */
export const partASign = route<{ real: true; signedAt: string }, void, Record<string, never>>()('POST', `${A}/worklist/part-a/:residentId/sign`, { honest: true });

// ── Nutrition ───────────────────────────────────────────────────────────────

export interface NutritionRow {
  residentId: string;
  name: string | null;
  trigger: 'weight_only' | 'function_only' | 'both';
  weight: { trigger: 'loss' | 'gain' | null; currentKg: number | null; pct30: number | null; pct180: number | null };
  functionDecline: boolean;
  functionDrop: number | null;
  room?: string | null;
  /** Null until a height is recorded (none is, anywhere, yet). */
  bmi?: number | null;
  /** The latest 30-second chair-stand count. */
  chairStand?: number | null;
  /** In a Part A stay with an MDS assessment reference date coming up. */
  partA?: boolean;
  ardAt?: string | null;
  glp1?: string | null;
  /** Dietitian, weekly weights, physician diagnosis, intake records: on file or not. */
  docs?: boolean[];
  /** Measurements staff recorded here, oldest first. */
  observations?: { lb: number; chair: number; by: string; at: string }[];
}

export interface NutritionWorklist {
  organizationId: string;
  rows: NutritionRow[];
  count: number;
  real: boolean;
  weightCoverage: number;
  source: string;
  generatedAt: string;
  /** Residents in the building (the denominator the design prints). */
  residentCount?: number;
  /** Residents with a recorded measurement on this instrument. */
  measuredCount?: number;
}

/** GET /worklist/nutrition: weight change (MDS K0300/K0310) and function decline. ?facility= for Aithora staff. */
export const nutritionWorklist = route<{ worklist: NutritionWorklist }, { facility?: string }>()('GET', `${A}/worklist/nutrition`);

/** POST /residents/:id/weight {weightKg}. */
export const recordWeight = route<{ weight: unknown }, void, { weightKg: number }>()('POST', `${A}/residents/:id/weight`);

/** POST /residents/:id/chair-stand {reps}: the 30-second sit-to-stand count. */
export const recordChairStand = route<{ real: true }, void, { reps: number }>()('POST', `${A}/residents/:id/chair-stand`, { honest: true });

// ── Benefits ────────────────────────────────────────────────────────────────

export interface BenefitItem {
  id: string;
  organizationId: string;
  residentId: string | null;
  type: string;
  /** 1 coverage (recert), 2 revenue (rebate), 3 resident savings (benefit). */
  consequenceClass: number;
  title: string;
  description: string | null;
  dueDate: string | null;
  cadence: string;
  status: string;
  ownerRole: string | null;
  dollarImpact: number | null;
  bucket: 'overdue' | 'due_7' | 'due_30' | 'due_90' | 'future' | 'no_date';
  /** "M. Doyle · Room 12", or null for a facility-wide item. */
  residentLabel?: string | null;
}

export interface BenefitQueue {
  items: BenefitItem[];
  total: number;
  buckets: Record<string, number>;
  dollarsAtRisk: number;
  /** The honesty flag: the rows are real; the dollar figures are modeled (basis). */
  real?: boolean;
  basis?: 'modeled';
}

/** GET /benefit-items?status=all: the benefits and rebates queue, soonest first. */
export const benefitItems = route<BenefitQueue, { status?: string; organizationId?: string; limit?: number }>()('GET', `${A}/benefit-items`);

/** PATCH /benefit-items/:id {status}: "Mark handled" is status complete. */
export const benefitItemUpdate = route<{ item: BenefitItem; spawnedId: string | null }, void, { status: 'complete' | 'open'; organizationId?: string }>()(
  'PATCH', `${A}/benefit-items/:id`);

export interface BenefitReferenceRow {
  k: string;
  v: string;
  d: string;
  y: string;
  s: string;
}

/** GET /benefit-items/reference: published facility rates and resident allowances, dated. */
export const benefitReference = route<{ real: true; facility: BenefitReferenceRow[]; resident: BenefitReferenceRow[]; notes: { facility: string; resident: string; volatility: string; lead?: string; gap?: string } }>()(
  'GET', `${A}/benefit-items/reference`, { honest: true });

// ── Referrals (mandatory reporter) ──────────────────────────────────────────

export interface Referral {
  id: string;
  organization_id: string;
  resident_id: string;
  incident_id: string | null;
  reporter_id: string | null;
  jurisdiction: string | null;
  aps_contact: string | null;
  aps_reference: string | null;
  narrative: string | null;
  checklist: Record<string, boolean | string> | null;
  status: 'DRAFT' | 'SUBMITTED' | 'ACKNOWLEDGED' | 'CLOSED' | 'WITHDRAWN';
  created_at: string;
  submitted_at: string | null;
  submitReadiness?: { ready: boolean; missingSteps: string[]; missingFields: string[] };
  residentName?: string | null;
  room?: string | null;
  /** Aithora across buildings: the building, where a room would be. */
  facilityName?: string | null;
  /** The Guardian signal it came from ("Unexplained bruising described"). */
  signalLabel?: string | null;
  severity?: 'HIGH' | 'MEDIUM' | 'LOW' | string | null;
  /** When the suspicion formed: the clock starts here. */
  suspicionAt?: string | null;
  /** 2 or 24: the statutory limit that applies. */
  limitHours?: number | null;
  /** A nursing home resident (Elder Justice Act 1150B) or not (state APS). */
  inScope1150B?: boolean | null;
  /** The label of the clock's basis ("1150B · 24h · no SBI"). */
  clockBasis?: string | null;
  /** The reporter's name as it is recorded. */
  reporterName?: string | null;
  /** The jurisdiction's contacts. */
  contacts?: { label: string; survey: string | null; aps: string; apsLabel: string } | null;
}

/** GET /reporter/referrals: the building's referral log. */
export const referrals = route<{ referrals: Referral[]; real?: boolean }, { organizationId?: string; status?: string }>()('GET', `${A}/reporter/referrals`);

export interface ReferralPatch {
  narrative?: string;
  checklist?: Record<string, boolean | string>;
  apsReference?: string;
  jurisdiction?: string;
  apsContact?: string;
  status?: 'SUBMITTED';
  organizationId?: string;
}

/** One reporter checklist step (mandatory-reporter.ts REPORTER_CHECKLIST). */
export interface ReporterChecklistItem {
  key: string;
  label: string;
  required: boolean;
}
/** GET /reporter/checklist: the reporter checklist, from the server (restored from production). */
export const reporterChecklist = route<{ checklist: ReporterChecklistItem[] }>()('GET', `${A}/reporter/checklist`);

/**
 * POST /reporter/referrals: a new referral from a blank form (restored from production).
 * Needs a qualifying basis: an Atlas-detected signal for the resident, or the staff
 * member's own observation (attestedBasis), which seeds the narrative. 422 otherwise.
 */
export const referralCreate = route<
  { referral: Referral },
  void,
  { residentId: string; attestedBasis?: string; narrative?: string; organizationId?: string }
>()('POST', `${A}/reporter/referrals`);

/** PATCH /reporter/referral/:id: save the draft, or file it (status SUBMITTED). */
export const referralUpdate = route<{ referral: Referral }, void, ReferralPatch>()('PATCH', `${A}/reporter/referral/:id`);

// ── "Pulled away" cover: defined once in ./cover-routes (src/aithora/v3/worklist/cover-routes.ts).
// The Worklist names the coverage read worklistCoverage and its live rows CoverLive.
export const worklistCoverage = facilityCoverage;
export type CoverLive = CoverageLive;

// ── New medicine watch: defined once in ./med-watch-routes (src/aithora/v3/worklist/med-watch-routes.ts).

export const worklistApi = {
  worklistMeasures,
  worklistCodes,
  feeSchedule,
  worklistCapture,
  worklistActions,
  worklistCreateItem,
  worklistAtRisk,
  worklistBoard,
  worklistHandoff,
  onShiftWithMe,
  careRequests,
  careRequestAcknowledge,
  careRequestComplete,
  medPass,
  medPassRecord,
  medPassUndo,
  medPassStrike,
  partANotes,
  partASign,
  nutritionWorklist,
  recordWeight,
  recordChairStand,
  benefitItems,
  benefitItemUpdate,
  benefitReference,
  referrals,
  referralUpdate,
  worklistCover,
  worklistCoverAccept,
  worklistCoverPass,
  worklistCoverTakeBack,
  worklistCoverage,
  facilityMedWatches,
  medWatchStepDone,
  medWatchClose,
  medWatchSign,
} as const;
